import {
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import { Accordion } from 'src/components/accordion';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import Radio from 'src/components/radio';
import { IVariableOptions } from 'src/context/wizard/types';

interface WizardVariableOptionItemProps {
  variableName: string;
  variableOptions: IVariableOptions;
  numberOfFilesToExport: number;
  onUpdate: (data: IVariableOptions) => void;
}

const renderVariableIcon = (status: 'success' | 'error' | 'warning') => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon className="size-6 text-green-500" />;
    case 'error':
      return <XCircleIcon className="size-6 text-red-500" />;
    case 'warning':
      return <ExclamationTriangleIcon className="size-6 text-yellow-500" />;
    default:
      return <ExclamationTriangleIcon className="size-6 text-yellow-500" />;
  }
};

const validateVariableOptions = (
  type: 'range' | 'list',
  from: number,
  to: number,
  list: string[],
  numberOfFilesToExport: number,
): { errorMessage: string; status: 'success' | 'error' | 'warning' } => {
  const errors: string[] = [];

  if (type === 'range') {
    if (from > to) {
      errors.push('From must be less than or equal to to');
    }
    if (from < 1) {
      errors.push('From must be greater than 0');
    }
    if (to < 1) {
      errors.push('To must be greater than 0');
    }
    if (to - from + 1 !== numberOfFilesToExport) {
      errors.push('Range must equal to the number of files to export');
    }
  }

  if (type === 'list') {
    if (list.length !== numberOfFilesToExport) {
      errors.push('List length must equal to the number of files to export');
    }
    if (list.some((line) => line.trim() === '')) {
      errors.push('List must not contain empty items');
    }
  }

  const errorMessage = errors.join('. ');
  const status =
    errorMessage === '' ? 'success' : ('error' as 'success' | 'error');

  return { errorMessage, status };
};

export default function WizardVariableOptionItem({
  variableName,
  variableOptions,
  numberOfFilesToExport,
  onUpdate,
}: WizardVariableOptionItemProps) {
  const handleTypeChange = (type: 'range' | 'list') => {
    const validation = validateVariableOptions(
      type,
      variableOptions.from,
      variableOptions.to,
      variableOptions.list,
      numberOfFilesToExport,
    );
    onUpdate({
      ...variableOptions,
      type,
      ...validation,
    });
  };

  const handleRangeChange = (field: 'from' | 'to', value: number) => {
    const newFrom = field === 'from' ? value : variableOptions.from;
    const newTo = field === 'to' ? value : variableOptions.to;
    const validation = validateVariableOptions(
      'range',
      newFrom,
      newTo,
      variableOptions.list,
      numberOfFilesToExport,
    );
    onUpdate({
      ...variableOptions,
      from: newFrom,
      to: newTo,
      ...validation,
    });
  };

  const handleListChange = (value: string) => {
    const list = value.split('\n').map((line) => line.trim());
    const validation = validateVariableOptions(
      'list',
      variableOptions.from,
      variableOptions.to,
      list,
      numberOfFilesToExport,
    );
    onUpdate({
      ...variableOptions,
      list,
      ...validation,
    });
  };

  // Reset range length to match file count, keep current `from` when possible
  const handleRefresh = () => {
    if (variableOptions.type === 'list') {
      const validation = validateVariableOptions(
        'list',
        variableOptions.from,
        variableOptions.to,
        variableOptions.list,
        numberOfFilesToExport,
      );
      onUpdate({
        ...variableOptions,
        ...validation,
      });
      return;
    }

    const from = variableOptions.from >= 1 ? variableOptions.from : 1;
    const to = from + numberOfFilesToExport - 1;
    const validation = validateVariableOptions(
      'range',
      from,
      to,
      variableOptions.list,
      numberOfFilesToExport,
    );
    onUpdate({
      ...variableOptions,
      type: 'range',
      from,
      to,
      ...validation,
    });
  };

  return (
    <Accordion
      title={`Variable: ${variableName}`}
      subtitle={variableOptions.errorMessage}
      action={renderVariableIcon(variableOptions.status)}
    >
      <div className="flex w-full flex-row items-start justify-between gap-2">
        <div className="flex w-full flex-row items-start justify-start gap-2">
          <div className="flex flex-row items-center justify-center gap-1">
            <Radio
              checked={variableOptions.type === 'range'}
              id={`${variableName}-range`}
              name={`${variableName}-type`}
              data-testid={`${variableName}-range`}
              onChange={() => handleTypeChange('range')}
            />
            <label
              htmlFor={`${variableName}-range`}
              className="text-base text-gray-900"
            >
              Range
            </label>
          </div>
          <div className="flex flex-row items-center justify-center gap-1">
            <Radio
              checked={variableOptions.type === 'list'}
              id={`${variableName}-list`}
              name={`${variableName}-type`}
              data-testid={`${variableName}-list`}
              onChange={() => handleTypeChange('list')}
            />
            <label
              htmlFor={`${variableName}-list`}
              className="text-base text-gray-900"
            >
              List
            </label>
          </div>
        </div>
        <div className="flex flex-row items-center justify-center gap-1">
          {variableOptions.status !== 'success' && (
            <Button
              type="button"
              color="blue"
              size="sm"
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          )}
        </div>
      </div>
      {variableOptions.type === 'range' && (
        <div className="flex w-full flex-row items-start justify-start gap-2">
          <FormInput
            id={`${variableName}-range-from`}
            name={`${variableName}-range-from`}
            label="From"
            type="number"
            value={variableOptions.from}
            onChange={(e) => {
              handleRangeChange('from', Number(e.target.value));
            }}
          />
          <FormInput
            id={`${variableName}-range-to`}
            name={`${variableName}-range-to`}
            label="To"
            type="number"
            value={variableOptions.to}
            onChange={(e) => {
              handleRangeChange('to', Number(e.target.value));
            }}
          />
        </div>
      )}
      {variableOptions.type === 'list' && (
        <div className="flex w-full flex-row items-start justify-start gap-2">
          <textarea
            id={`${variableName}-list`}
            name={`${variableName}-list`}
            className="w-full rounded-md border border-gray-200 p-2"
            rows={5}
            value={variableOptions.list.join('\n')}
            onChange={(e) => {
              handleListChange(e.target.value);
            }}
          />
        </div>
      )}
    </Accordion>
  );
}
